import {
  DashboardPeriod,
  DashboardProgramCard,
} from '../dashboardTypes'
import { primaryProgramType } from './ProgramCard'

export const PROGRAM_TYPE_ORDER = [
  'TRANSIT_PASS',
  'FLEX',
  'MICROMOBILITY',
  'GRH',
  'OTP',
  'COUPON',
  'REPORTED_USAGE',
] as const

const TYPE_COPY: Record<string, { title: string; description: string }> = {
  TRANSIT_PASS: {
    title: 'Transit passes',
    description: 'Agency transit cards for the daily commute',
  },
  FLEX: {
    title: 'Flexible stipends',
    description: 'Open commute — any mode, employer-funded',
  },
  MICROMOBILITY: {
    title: 'Micromobility',
    description: 'Bikeshare, scooters and e-bike subsidies',
  },
  GRH: {
    title: 'Guaranteed ride home',
    description: 'Emergency rides when plans change',
  },
  OTP: {
    title: 'One-time purchase',
    description: 'One-time commute purchases',
  },
  COUPON: {
    title: 'Coupons',
    description: 'Partner memberships and promo codes',
  },
  REPORTED_USAGE: {
    title: 'Reported usage',
    description: 'Uploaded rideshare, bike, and commute spend',
  },
}

export interface ProgramTypeSection {
  type: string
  title: string
  description: string
  programs: number
  enrolled: number
  sitesActive: number
  sitesTotal: number
  spendCents: number
  cards: DashboardProgramCard[]
}

const spendFor = (card: DashboardProgramCard, period: DashboardPeriod) =>
  card.byPeriod?.[period]?.moneySpentCents ?? 0

export const programTypeSections = (
  cards: DashboardProgramCard[],
  period: DashboardPeriod
): ProgramTypeSection[] => {
  const grouped = new Map<string, DashboardProgramCard[]>()
  cards.forEach((card) => {
    const type = primaryProgramType(card.types) || 'OTHER'
    const bucket = grouped.get(type) ?? []
    bucket.push(card)
    grouped.set(type, bucket)
  })

  const order = [
    ...PROGRAM_TYPE_ORDER.filter((type) => grouped.has(type)),
    ...[...grouped.keys()].filter(
      (type) => !PROGRAM_TYPE_ORDER.includes(type as (typeof PROGRAM_TYPE_ORDER)[number])
    ),
  ]

  return order.map((type) => {
    const sectionCards = [...(grouped.get(type) ?? [])].sort(
      (a, b) =>
        b.enrolled - a.enrolled ||
        a.superProgramName.localeCompare(b.superProgramName)
    )
    const activeSites = new Set<string>()
    let sawSites = false
    sectionCards.forEach((card) => {
      ;(card.siteEnrollments ?? []).forEach((site) => {
        sawSites = true
        if (site.enrolled > 0) {
          activeSites.add(site.id)
        }
      })
    })
    const sitesActive = sawSites
      ? activeSites.size
      : sectionCards.reduce((sum, card) => sum + card.sitesActive, 0)
    const sitesTotal = Math.max(
      sitesActive,
      ...sectionCards.map((card) => card.sitesTotal)
    )
    const copy = TYPE_COPY[type] ?? {
      title: type.replace(/_/g, ' ').toLowerCase(),
      description: '',
    }
    return {
      type,
      title: copy.title,
      description: copy.description,
      programs: sectionCards.length,
      enrolled: sectionCards.reduce((sum, card) => sum + card.enrolled, 0),
      sitesActive,
      sitesTotal,
      spendCents: sectionCards.reduce(
        (sum, card) => sum + spendFor(card, period),
        0
      ),
      cards: sectionCards,
    }
  })
}
